/** How it felt (DESIGN §27.1, §27.2): effort 1–10 and sore or hurt reports, as the phones send them. */
import { addDays, mondayOf } from "./days.js";
import type { BodyArea, BodyReport, Session } from "./types.js";

/** The CR-10 scale's words, 1 to 10. */
export const EFFORT_WORDS = ["Very easy", "Easy", "Moderate", "Somewhat hard", "Hard", "Hard", "Very hard", "Very hard", "Very, very hard", "Max"];

export const BODY_AREA_LABEL: Record<BodyArea, string> = {
  hamstring: "Hamstring",
  quad: "Quad",
  knee: "Knee",
  calf: "Calf",
  achilles: "Achilles",
  shin: "Shin",
  ankle: "Ankle",
  foot: "Foot",
  hipFlexor: "Hip flexor",
  groin: "Groin",
  glute: "Glute",
  lowerBack: "Lower back",
  upperBack: "Upper back",
  shoulder: "Shoulder",
  elbow: "Elbow",
  wrist: "Wrist",
  neck: "Neck",
  other: "Other",
};

export const BODY_AREAS = Object.keys(BODY_AREA_LABEL) as BodyArea[];

const MIN_MINUTES = 10;
const MAX_MINUTES = 240;

/** The effort reported, or null when none or not a whole number from 1 to 10. */
export function effortOf(session: Pick<Session, "effort"> | null | undefined): number | null {
  const e = session?.effort;
  return typeof e === "number" && Number.isInteger(e) && e >= 1 && e <= 10 ? e : null;
}

export function effortWord(effort: number): string {
  return EFFORT_WORDS[effort - 1] ?? "";
}

/** `RPE 7`, or null when no effort was reported. */
export function rpeText(session: Pick<Session, "effort"> | null | undefined): string | null {
  const e = effortOf(session);
  return e == null ? null : "RPE " + e;
}

/** The report, its unknown areas dropped; null when it is not of the shape the phone sends. */
export function bodyOf(session: Pick<Session, "body"> | null | undefined): BodyReport | null {
  const b = session?.body as Partial<BodyReport> | null | undefined;
  if (!b || typeof b !== "object" || (b.level !== "sore" && b.level !== "hurt")) return null;
  const areas: BodyArea[] = [];
  for (const a of Array.isArray(b.areas) ? b.areas : []) if (a in BODY_AREA_LABEL && !areas.includes(a)) areas.push(a);
  const side = b.side === "left" || b.side === "right" || b.side === "both" ? b.side : null;
  const note = typeof b.note === "string" && b.note.trim() ? b.note.trim().slice(0, 200) : null;
  return { level: b.level, areas, side, note };
}

/**
 * `Left hamstring, calf`: a side names the first area, a real part; with only Other, or both sides,
 * it follows (`Hamstring, calf, both sides`). Lower case all through for a line that runs on.
 */
export function bodyAreasText(body: Pick<BodyReport, "areas" | "side">, lower = false): string {
  const labels = body.areas.map((a, i) => (i === 0 ? BODY_AREA_LABEL[a] : BODY_AREA_LABEL[a].toLowerCase()));
  let text = labels.join(", ");
  const side = body.side;
  if (side === "left" || side === "right") {
    if (body.areas[0] && body.areas[0] !== "other") text = (side === "left" ? "Left " : "Right ") + text.charAt(0).toLowerCase() + text.slice(1);
    else text = text ? text + ", " + side + " side" : side.charAt(0).toUpperCase() + side.slice(1) + " side";
  } else if (side === "both") text = text ? text + ", both sides" : "Both sides";
  return lower ? text.toLowerCase() : text;
}

export function levelWord(level: BodyReport["level"]): string {
  return level === "hurt" ? "Hurt" : "Sore";
}

/** `Hurt · Left hamstring, calf`, or just `Sore` when no area was chosen. */
export function bodyText(body: BodyReport): string {
  const where = bodyAreasText(body);
  return levelWord(body.level) + (where ? " · " + where : "");
}

const REFERENCE = Date.UTC(2001, 0, 1) / 1000;

/** Seconds since 2001-01-01 (the phone's dates) as a JS time; null for anything else. */
export function phoneTime(seconds: unknown): number | null {
  return typeof seconds === "number" && Number.isFinite(seconds) ? (REFERENCE + seconds) * 1000 : null;
}

/** Whole minutes from start to finish, only between 10 and 240: a run marked done after the fact has none. */
export function minutesOf(session: Pick<Session, "startedAt" | "finishedAt"> | null | undefined): number | null {
  const start = phoneTime(session?.startedAt);
  const end = phoneTime(session?.finishedAt);
  if (start == null || end == null) return null;
  const minutes = Math.round((end - start) / 60_000);
  return minutes >= MIN_MINUTES && minutes <= MAX_MINUTES ? minutes : null;
}

/** Effort × minutes; null when either is missing. */
export function sessionLoad(session: Pick<Session, "effort" | "startedAt" | "finishedAt"> | null | undefined): number | null {
  const e = effortOf(session);
  const m = minutesOf(session);
  return e == null || m == null ? null : e * m;
}

type Dated = { date: string; title?: string; session?: Session | null };

export interface WeekEffort {
  week: string;
  /** Σ effort × minutes; null when nothing that week reported an effort (no bar, not a zero). */
  load: number | null;
  /** The average effort reported, or null. */
  effort: number | null;
  sessions: number;
}

/** Each Monday's week of reported effort. */
export function weeklyEffort(workouts: Dated[], mondays: string[]): WeekEffort[] {
  const by = new Map<string, { load: number; sum: number; n: number }>(mondays.map((m) => [m, { load: 0, sum: 0, n: 0 }]));
  for (const w of workouts) {
    const e = effortOf(w.session);
    const week = by.get(mondayOf(w.date));
    if (e == null || !week) continue;
    week.n += 1;
    week.sum += e;
    week.load += sessionLoad(w.session) ?? 0;
  }
  return mondays.map((week) => {
    const x = by.get(week)!;
    return { week, load: x.n ? x.load : null, effort: x.n ? x.sum / x.n : null, sessions: x.n };
  });
}

export function hasEffort(workouts: { session?: Session | null }[]): boolean {
  return workouts.some((w) => effortOf(w.session) != null || bodyOf(w.session) != null);
}

/** `1,240`. */
export function effortLoadText(load: number): string {
  return Math.round(load).toLocaleString("en-US");
}

/** `6.5`, `7`. */
export function averageText(effort: number): string {
  return String(Math.round(effort * 10) / 10);
}

export interface DatedReport {
  date: string;
  title: string;
  body: BodyReport;
}

/** Sore and hurt reports from the Monday three weeks before `today`'s, newest first. */
export function recentReports(workouts: Dated[], today: string, weeks = 4): DatedReport[] {
  const from = addDays(mondayOf(today), -7 * (weeks - 1));
  const out: DatedReport[] = [];
  for (const w of workouts) {
    if (w.date < from || w.date > today) continue;
    const body = bodyOf(w.session);
    if (body) out.push({ date: w.date, title: (w.title || w.session?.title || "").trim(), body });
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
